import type { Schema } from "./api";
import { decimalText } from "./resourcePrice.ts";

export type SvorRemark = Schema["SvorRemark"];
export type SvorActionTopic = "repeat" | "match" | "data" | "change" | "check";
export type SvorActionFilter = "all" | SvorActionTopic | "previous";
type Attention = { topic: SvorActionTopic; action: string; explanation: string; tone: "amber" | "blue" };
export const svorActionTopics: SvorActionTopic[] = ["repeat", "match", "data", "change", "check"];
export const svorActionLabels: Record<SvorActionTopic, string> = {
  repeat: "Проверить повторы", match: "Сопоставить позиции", data: "Уточнить данные",
  change: "Проверить изменения", check: "Проверить основания",
};
const attention: Record<string, Attention> = {
  duplicate: { topic: "repeat", action: "Проверить повтор позиции", tone: "amber", explanation: "Сверьте строки источника. Повтор не объединяется автоматически." },
  collision: { topic: "repeat", action: "Уточнить одинаковые номера", tone: "amber", explanation: "Один номер встречается с разным содержанием. Строки остаются раздельными." },
  unmatched: { topic: "match", action: "Проверить соответствие позиции", tone: "blue", explanation: "Сверьте позицию изменения с исходной сметой. Соответствие не установлено." },
  unmatched_number: { topic: "match", action: "Проверить соответствие позиции", tone: "blue", explanation: "Совпадения номера недостаточно: проверьте содержание, шифр и раздел. Результат обработки — в основании." },
  units: { topic: "data", action: "Уточнить единицы", tone: "amber", explanation: "Проверьте единицы в исходной смете и изменении. Интерфейс не пересчитывает объёмы." },
  unknown: { topic: "data", action: "Уточнить объём", tone: "amber", explanation: "Найдите объём в источнике. Отсутствующее значение не считается нулём." },
  unknown_base: { topic: "data", action: "Уточнить исходный объём", tone: "amber", explanation: "Для расчёта нужен объём исходной позиции. Пустое значение не заменяется нулём." },
  negative: { topic: "change", action: "Проверить знак исключения", tone: "amber", explanation: "Проверьте запись исключения и способ обработки знаков в источнике И." },
  below_zero: { topic: "change", action: "Проверить объём изменений", tone: "amber", explanation: "Получился отрицательный итог. Сверьте исходный объём, исключения и добавления." },
  malformed: { topic: "data", action: "Проверить строку источника", tone: "amber", explanation: "Проверьте исходную строку и назначение колонок: данные не удалось разобрать." },
};
const fallback: Attention = { topic: "check", action: "Проверить основание", tone: "amber", explanation: "Откройте исходное замечание и его источники. Тип замечания не классифицирован интерфейсом." };

/** Only the server kind assigns an action; labels, numbers and free text do not establish identity. */
export function svorRemarkAttention(remark: Pick<SvorRemark, "kind">): Attention {
  return Object.prototype.hasOwnProperty.call(attention, remark.kind) ? attention[remark.kind] : fallback;
}

/** Both server lists remain intact and ordered. Repeated keys are separate records, not duplicates to erase. */
export function svorProtocolEntries(round: Pick<Schema["SvorRoundOut"], "remarks" | "carried">) {
  return [
    ...round.remarks.map((remark, index) => ({ id: `current:${index}:${remark.key}`, remark, previous: false })),
    ...round.carried.map((remark, index) => ({ id: `previous:${index}:${remark.key}`, remark, previous: true })),
  ];
}

export function svorRemarkQuantity(remark: Pick<SvorRemark, "position">): string {
  const { quantity, unit } = remark.position;
  return quantity == null || quantity.trim() === ""
    ? "Не установлен"
    : `${decimalText(quantity)} ${unit}`.trim();
}

export function svorRemarkContext(previous: boolean, latest: boolean): string {
  if (previous) return latest ? "Перенесено из прошлого раунда · не означает устранение" : "В снимке: перенесено из прошлого раунда";
  return latest ? "Замечание источников раунда" : "Замечание в сохранённом раунде";
}
